import { Loader2, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { SkeletonRows } from '@/components/ui/skeleton'
import { useDebouncedInput } from '@/hooks/useDebouncedInput'
import { useSalesLeads } from '@/models/salesLead'
import { type TelephonyCallVendor, useAttachTelephonyCall } from '@/models/telephonyCalls'

interface Props {
  callId: string | null
  vendor: TelephonyCallVendor | null
  hint: string
  onClose: () => void
}

export function AttachToLeadDialog({ callId, vendor, hint, onClose }: Props) {
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const { inputValue, setInputValue } = useDebouncedInput('', setDebouncedSearch)
  const { leads, isLoading } = useSalesLeads(debouncedSearch ? { search: debouncedSearch } : undefined)
  const attach = useAttachTelephonyCall()
  const open = callId !== null

  function handleClose() {
    setInputValue('')
    setDebouncedSearch('')
    onClose()
  }

  function handleAttach(leadId: string) {
    if (!callId || !vendor) return
    attach.mutate(
      { callId, vendor, leadId },
      {
        onSuccess: () => {
          toast.success('Call attached to lead')
          handleClose()
        },
        onError: (err) => {
          const maybe = err as { response?: { data?: { error?: string } } }
          toast.error(maybe.response?.data?.error ?? 'Could not attach call')
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach call to lead</DialogTitle>
          <DialogDescription>
            Find the lead this call belongs to. {hint && <span className="font-mono">{hint}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
          <Input
            autoFocus
            placeholder="Search by name or DOT number..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="max-h-80 overflow-y-auto rounded-md border">
          {isLoading && <SkeletonRows className="p-3" rows={4} rowClassName="h-11" />}
          {!isLoading && debouncedSearch && leads.length === 0 && (
            <div className="text-muted-foreground p-4 text-sm">No leads match.</div>
          )}
          {!isLoading && !debouncedSearch && (
            <div className="text-muted-foreground p-4 text-sm">Start typing to search…</div>
          )}
          {!isLoading &&
            leads.map((lead) => (
              <Button
                key={lead.id}
                variant="ghost"
                disabled={attach.isPending}
                onClick={() => handleAttach(lead.id)}
                className="hover:bg-accent flex h-auto w-full items-center justify-between rounded-none border-b px-3 py-2 text-left last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{lead.legalName}</div>
                  <div className="text-muted-foreground truncate text-xs">DOT {lead.dotNumber}</div>
                </div>
                {attach.isPending && attach.variables.leadId === lead.id && (
                  <Loader2 className="size-4 animate-spin" />
                )}
              </Button>
            ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
